/**
 * Global auth-failure bus.
 *
 * When any oRPC call returns UNAUTHORIZED, or `whoami` returns a null
 * user for a logged-in-seeming tenant, we fire a single auth-failure
 * event and let subscribers (UserProvider) do a full logout + redirect.
 *
 * Why a global bus instead of wiring logout into the oRPC client directly:
 *   - The Providers tree mounts per-route group, so there can be multiple
 *     UserProvider instances during a navigation.
 *   - The oRPC client is a module-level singleton with no React context.
 *   - A plain event bus avoids circular imports between the client and
 *     the provider, and lets us debounce concurrent failures.
 *
 * The `isLoggingOut` guard prevents a cascade of 401s (e.g. when several
 * queries are in flight at once) from all firing logout in parallel.
 *
 * The `verifyBeforeLogout` mechanism avoids force-logging the user out
 * on transient 401s (e.g. during a backend restart in dev). A single
 * whoami call confirms whether the session is genuinely dead before we
 * nuke cookies and redirect.
 */

type Handler = () => void

const handlers = new Set<Handler>()
let isLoggingOut = false
let isVerifying = false

export function onAuthFailure(handler: Handler): () => void {
  handlers.add(handler)
  return () => {
    handlers.delete(handler)
  }
}

function doFire(): void {
  isLoggingOut = true
  for (const handler of handlers) {
    try {
      handler()
    } catch (err) {
      console.warn('[auth-failure] handler threw:', err)
    }
  }
  // Reset after 2s so a fresh login can later fire new failure events.
  setTimeout(() => {
    isLoggingOut = false
  }, 2000)
}

/**
 * Fire an auth-failure event. Before doing a full logout, we verify
 * the session is actually dead by calling whoami. This avoids
 * force-logging the user out on transient 401s (backend restart,
 * temporary DB hiccup, etc.).
 *
 * @param backendUrl — BACKEND_URL from the oRPC client (avoids circular import)
 */
export function fireAuthFailure(backendUrl?: string): void {
  if (isLoggingOut || isVerifying) return

  if (!backendUrl) {
    // No backend URL → can't verify, fire directly
    doFire()
    return
  }

  isVerifying = true

  // Give the backend a moment to come back (e.g. after a restart)
  // then check if the session is actually dead.
  setTimeout(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/v1/user/whoami`, {
        credentials: 'include',
      })
      if (res.status === 401) {
        // Session is genuinely dead — log out
        doFire()
      }
      // Any other response (200, 500, network recovery) means the
      // session might still be valid — don't log out.
    } catch {
      // Network error (backend still down) — don't log out yet.
      // The user's queries will retry via TanStack Query and the
      // next 401 will re-trigger this check.
    } finally {
      isVerifying = false
    }
  }, 1500)
}

/**
 * Paths where the app should NEVER redirect the user on auth failure —
 * they're already on a public page, or they're in the middle of logging in.
 */
const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/auth',
  '/401',
  '/404',
  '/maintenance',
  '/coming-soon',
  '/forgot-password',
  '/reset-password',
  '/setup-2fa',
]

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}
